import React from 'react';
import { Play, Headphones, Download, Calendar, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const MediaCard = ({ media, onPlay, onDownload }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCategoryLabel = (slug) => {
    const labels = {
      'sunday-service': 'Sunday Service',
      'bible-study': 'Bible Study',
      'special-events': 'Special Events',
      'youth-ministry': 'Youth Ministry'
    };
    return labels[slug] || slug;
  };

  return (
    <div 
      className="media-card rounded-sm border border-border bg-card overflow-hidden opacity-0 animate-fade-in"
      data-testid={`media-card-${media.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted overflow-hidden">
        <img 
          src={media.thumbnail_url} 
          alt={media.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
          <button
            onClick={() => onPlay(media)}
            className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:scale-110 transition-transform"
            data-testid={`play-button-${media.id}`}
          >
            {media.media_type === 'video' ? (
              <Play className="w-6 h-6 fill-current ml-1" />
            ) : (
              <Headphones className="w-6 h-6" />
            )}
          </button>
        </div>
        <Badge 
          variant="secondary" 
          className="absolute top-3 left-3 uppercase text-xs tracking-wider"
        >
          {media.media_type}
        </Badge>
      </div>

      {/* Content */}
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(media.service_date)}</span>
        </div>
        
        <h3 className="font-serif text-lg md:text-xl font-semibold text-foreground mb-2 line-clamp-2">
          {media.title}
        </h3>
        
        {media.speaker && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <User className="w-4 h-4" />
            <span>{media.speaker}</span>
          </div>
        )}
        
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {media.description || getCategoryLabel(media.category)}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <span className="text-xs text-muted-foreground">
            {formatFileSize(media.file_size)}
          </span>
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onDownload(media)}
              data-testid={`download-button-${media.id}`}
            >
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={() => onPlay(media)}
              data-testid={`play-card-button-${media.id}`}
            >
              {media.media_type === 'video' ? (
                <Play className="w-4 h-4 mr-1" />
              ) : (
                <Headphones className="w-4 h-4 mr-1" />
              )}
              Play
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaCard;
